/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';

import { useBenefitsStore } from 'src/stores/benefits/benefits.store';
import { useFilterStore } from 'src/stores/filters/filters.store';
import { Option } from 'src/types/common/option';

export const useBenefitFilter = (getData: () => void) => {
  const { filters, setFilters, removeFilter } = useFilterStore((state) => state);
  const { categoriesOptions, levelsOptions } = useBenefitsStore((state) => state);

  const [searchValue, setSearchValue] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Option[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<Option[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<Option[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Option | undefined>();

  const typeOptions: Option[] = [
    { label: 'Voucher', value: 'voucher' },
    { label: 'Informativo', value: 'informative' },
    { label: 'Estacionamento', value: 'parking' },
  ];

  const statusOptions: Option[] = [
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
  ];

  const debouncedSearch = debounce((value: string) => {
    if (value.length > 0 && value !== '') {
      setFilters({ ...(value && { search: value }) });
    } else {
      removeFilter('search');
    }
  }, 500);

  const onSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSelectCategory = (options: Option[]) => {
    setSelectedCategories(options);
    setFilters({ ...(options && { categories: options.map((o) => o.value) }) });
  };

  const handleSelectLevel = (options: Option[]) => {
    setSelectedLevels(options);
    setFilters({ ...(options && { levels: options.map((o) => o.value) }) });
  };

  const handleSelectType = (options: Option[]) => {
    setSelectedTypes(options);
    // Backend expects single type value, not array
    if (options && options.length > 0) {
      setFilters({ type: options[0].value });
    } else {
      removeFilter('type');
    }
  };

  const handleSelectStatus = (option: Option | undefined) => {
    setSelectedStatus(option);
    if (!option) {
      removeFilter('status');
    } else {
      setFilters({ ...(option && { status: option.value }) });
    }
  };

  const handleRemoveCategoryFilter = (value: string) => {
    const newCategories = selectedCategories.filter((cat) => cat.value !== value);
    setSelectedCategories(newCategories);
    if (newCategories.length === 0) {
      removeFilter('categories');
    } else {
      setFilters({ categories: newCategories.map((o) => o.value) });
    }
  };

  const handleRemoveLevelFilter = (value: string) => {
    const newLevels = selectedLevels.filter((level) => level.value !== value);
    setSelectedLevels(newLevels);
    if (newLevels.length === 0) {
      removeFilter('levels');
    } else {
      setFilters({ levels: newLevels.map((o) => o.value) });
    }
  };

  const handleRemoveTypeFilter = (value: string) => {
    const newTypes = selectedTypes.filter((type) => type.value !== value);
    setSelectedTypes(newTypes);
    if (newTypes.length === 0) {
      removeFilter('type');
    } else {
      setFilters({ type: newTypes[0]?.value });
    }
  };

  const handleRemoveAllFilters = () => {
    setSearchValue('');
    setSelectedCategories([]);
    setSelectedLevels([]);
    setSelectedTypes([]);
    setSelectedStatus(undefined);
    setFilters({});
    removeFilter('status');
    removeFilter('categories');
    removeFilter('levels');
    removeFilter('type');
    removeFilter('search');
  };

  useEffect(() => {
    getData();
  }, [filters]);

  return {
    categoriesOptions,
    levelsOptions,
    typeOptions,
    statusOptions,
    selectedCategories,
    selectedLevels,
    selectedTypes,
    selectedStatus,
    searchValue,
    handleSelectCategory,
    handleSelectLevel,
    handleSelectType,
    handleSelectStatus,
    handleRemoveCategoryFilter,
    handleRemoveLevelFilter,
    handleRemoveTypeFilter,
    handleRemoveAllFilters,
    onSearch,
  };
};
